import { useCallback, useEffect, useRef } from 'react';

type Arguments<T> = T extends (...args: infer U) => unknown ? U : never;

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const useDebouncedCallback = <Callback extends Function>(
  callback: Callback,
  delay: number | null,
  immediately: boolean = false,
) => {
  const savedCallback = useRef<Callback>(callback);
  const timerId = useRef<NodeJS.Timeout | null>(undefined);
  const skipIteration = useRef(false);

  useEffect(() => {
    savedCallback.current = callback;
  });

  const handleTimeout = useCallback(
    (callbackFn: VoidFunction) => {
      if (timerId.current) {
        clearTimeout(timerId.current);
      }

      if (delay === null) {
        return;
      }

      timerId.current = setTimeout(callbackFn, delay);
    },
    [delay],
  );

  return useCallback(
    (...args: Arguments<Callback>) => {
      if (immediately) {
        if (!skipIteration.current) {
          savedCallback.current.call(this, ...args);
          skipIteration.current = true;
        }

        handleTimeout(() => (skipIteration.current = false));

        return;
      }

      handleTimeout(() => {
        savedCallback.current.call(this, ...args);
      });
    },
    [handleTimeout, immediately],
  );
};
